/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Box,
  Button,
  Chip,
  Dialog, DialogContent,
  Divider,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  ThemeOptions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium, GridCellEditStopParams, GridCellParams, GridColumnResizeParams,
  GridDensity,
  GridFilterItem,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import React, { FC, ForwardedRef, forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import OrderSplitForm from 'src/components/V2OrderSplitForm';
import ShipmentOrderLegForm from 'src/components/V2ShipmentOrderLegForm';
import { actionBarHeight, appBarHeight } from 'src/constants/layout';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import numberDecorator from 'src/decorators/number.decorator';
import { OrderResponseEntity, OrderResponseEntityWithNotAllocReason } from 'src/entities/Order.response.entity';
import { OrderAssignment, OrderEntity } from 'src/entities/orderEntity';
import { OrderSplitEntity } from 'src/entities/orderSplitEntity';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithTruckAndDriverEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { useDeleteAllOrdersMutation } from 'src/hooks/useDeleteAllOrders.mutation';
import { useOrdersRequest as usePaginationRequest, queryKey } from 'src/hooks/useOrders.request';
import { useMutationOrderAssignment } from 'src/hooks/useQueryOrderAssignments';
import { useMutationOrder } from 'src/hooks/useQueryOrders';
import { Operations } from 'src/models/Operations';
import { dataGridUtil } from 'src/utils/dataGrid.util';
import numberUtil from 'src/utils/number.util';
import operatorValueUtil from 'src/utils/operatorValue.util';
import { orderRequestUtil } from 'src/utils/orderRequest.util';
import stringUtil from 'src/utils/string.util';

import { SelectAllSnackbars } from '../common/SelectAllSnackbars';
import CoordinationEditDialog from '../V2PlacesPresenter/PositionEditDialog';

import { Columns } from './Columns';
import CustomToolbar from './CustomToolbar';
import PdfSettingDialog from './PdfSettingDialog';

interface CustomToolbarProps {
  startOn?: Date;
  endOn?: Date;
  removeSelectedOrders: () => Promise<void>;
  resetSelectedOrders: () => Promise<void>;
  toggleDialogIsOpen: () => void;
  setIsLoading: (value: boolean | ((prevState: boolean) => boolean)) => void;
  isLoading: boolean;
  handleDateRange: (range: [Date, Date]) => void;
  canDeleteAll: boolean;
  deleteAll: (settledFnk: () => void) => void;
  handleDownloadExcel: () => Promise<void>;
  isPlanningView?: boolean;
  setFetchPage: React.Dispatch<React.SetStateAction<number>>;
  onClickDownloadOutsourcingPdf: () => void;
}

type Props = {
  startDate: string;
  endDate: string;
  isPlanningView?: boolean;
  setOrderGridApiRef?: (apiRef: React.MutableRefObject<GridApiPremium>) => void;
  notAllocDeliveries?: PlanningsDeliveryEntity[];
  notAllocTrucks?: PlanningsTruckEntity[];
  notAllocDrivers?: PlanningsDriverEntity[];
  deliveryWithTruckAndDriverEntities?: PlanningsDeliveryWithTruckAndDriverEntity[];
  requestForcePlanning?: (delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[]) => void;
  resetQuery?: () => void;
  forcePlanningQueue?: { delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[], latestAlgorithmRequestVersion: number }[];
  currentForcePlanning?: { delivery: PlanningsDeliveryWithTruckAndDriverEntity, orderIds: number[], latestAlgorithmRequestVersion: number };
  partnerCompanyEntities?: PartnerCompanyEntity[];
  driverEntities?: PlanningsDriverEntity[];
  truckEntities?: PlanningsTruckEntity[];
  ref?: ForwardedRef<{ afterResetQuery: () => void; } | null>;
  afterResetOrders?: () => void;
}

const V2ShipmentOrderLegsPresenter: FC<Props> = memo(
  forwardRef(({
    startDate,
    endDate,
    isPlanningView,
    setOrderGridApiRef,
    notAllocDeliveries,
    notAllocTrucks,
    notAllocDrivers,
    deliveryWithTruckAndDriverEntities,
    requestForcePlanning,
    resetQuery,
    forcePlanningQueue,
    currentForcePlanning,
    partnerCompanyEntities,
    driverEntities,
    truckEntities,
    afterResetOrders,
  }, ref) => {
  const localStorageItemPrefix = `${isPlanningView ? 'map-' : ''}order-columns`;
  const theme: Theme & ThemeOptions = useTheme();
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const defaultRowHeight = 52;
  const queryClient = useQueryClient();

  const DEFAULT_HIDDEN_COLUMNS = useMemo(() => ({
    loading_latitude: false,
    loading_longitude: false,
    unloading_latitude: false,
    unloading_longitude: false,
  }), []);

  const DEAFULT_PAR_PAGE = 50;

  const [startOn, setStartOn] = useState<Date | undefined>(undefined);
  const [endOn, setEndOn] = useState<Date | undefined>(undefined);
  const [startOnStr, setStartOnStr] = useState('');
  const [endOnStr, setEndOnStr] = useState('');
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [isLoading, setIsLoading]: [boolean, ((value: (((prevState: boolean) => boolean) | boolean)) => void)] = useState<boolean>(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [orderSplitEntity, setOrderSplitEntity] = useState<OrderSplitEntity | undefined>(undefined);
  const [splitDialogIsOpen, setSplitDialogIsOpen] = useState<boolean>(false);
  const [editTargetOrder, setEditTargetOrder] = useState<OrderEntity>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEAFULT_PAR_PAGE);
  const [rowCount, setRowCount] = useState<number>(0);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [canDeleteAll, setCanDeleteAll] = useState<boolean>(false);
  const [rowData, setRowData] = useState<OrderResponseEntityWithNotAllocReason[]>([]);
  const [ltlCarriers, setLtlCarrier] = useState<PartnerCompanyEntity[]>([]);
  const [hasNoDeliveryDrivers, setHasNoDeliveryDrivers] = useState<PlanningsDriverEntity[]>([]);
  const [orderEntities, setOrderEntities] = useState<OrderResponseEntity[]>([]);
  const apiRef = useGridApiRef();

  const [fetchPage, setFetchPage] = useState<number>(0);
  // const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(page, sortCondition, searchCondition, startOn, endOn, pageSize);
  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(fetchPage, '', '', startOn, endOn, 500, groupBy);
  // const { data: allRequestData } = usePaginationRequest(0, sortCondition, searchCondition, startOn, endOn, Number.MAX_SAFE_INTEGER);
  // const allRequestData = requestData;
  const { updateOrder, deleteOrders, operationBulkResetting } = useMutationOrder();
  const { update: updateOrderAssignment } = useMutationOrderAssignment();
  const { post } = useDeleteAllOrdersMutation();

  useImperativeHandle(ref, () => ({
    afterResetQuery: () => {
      void queryClient.resetQueries({
        queryKey: [queryKey]
      });
      setFetchPage(0);
    }
  }));

  useEffect(() => {
    if (!requestData) return;
    if (requestData.data.length === 0) {
      if (requestData.totalCount === 0) {
        setOrderEntities([]);
      }
      return;
    }

    const runnings = [...(forcePlanningQueue || []), currentForcePlanning].filter((it) => it);
    setOrderEntities((prev) => {
      const newEntities = [...prev.filter((it) => requestData.allIds.includes(it.id))];
      requestData.data.forEach((newData) => {
        const processing = runnings?.find((queue) => queue?.orderIds.includes(newData.id));
        if (processing) {
          const { delivery } = processing;
          const { truck, driver } = delivery;
          newData = {
            ...newData,
            order_operations_count: 1,
            truck_json: JSON.stringify([{
              id: truck?.id,
              license_plate_value: truck?.temporary ? '車両未設定' : truck?.licensePlateValue,
              temporary: truck?.temporary,
            }]),
            drivers_json: JSON.stringify([{
              id: driver?.id,
              name: driver?.temporary ? 'ドライバー未設定' : driver?.name,
              temporary: driver?.temporary,
            }]),
            order_assignments_json: JSON.stringify({
              id: 1,
              assignment: truck?.temporary ? '車両未設定' : truck?.licensePlateValue,
              truck_number: truck?.truckNumber,
              driver_name: driver?.temporary ? 'ドライバー未設定' : driver?.name,
            }),
          };
        }
        const index = newEntities.findIndex((it) => it.id === newData.id);
        if (index === -1) {
          newEntities.push(newData);
        } else {
          newEntities[index] = newData;
        }
      });
      return newEntities;
    });
    setFetchPage((prev) => {
      const nextPage = prev + 1;
      if (nextPage <= requestData.totalPageCount) {
        return nextPage;
      }
      return prev;
    });
  }, [requestData, currentForcePlanning, forcePlanningQueue]);

  const deleteAll = (settledFnk: () => void) => {
    if (startOn === undefined || endOn === undefined) return;

    if (window.confirm(`選択された ${rowCount} 件の注文を削除します。よろしいですか？`)) {
      setIsLoading(true);
      setSelectedOrderIds([]);

      post.mutate(({ startOn, endOn }), {
        onSuccess: () => {
          enqueueSnackbar('すべて削除しました');
        },
        onError: (error: AxiosError<{ message: string; }>) => {
          enqueueSnackbar(error.response?.data?.message);
        },
        onSettled: () => {
          setFetchPage(0);
          void queryClient.resetQueries({
            queryKey: [queryKey]
          })
            .finally(() => {
              settledFnk();
              setIsLoading(false);
            });
        }
      });
    }
  };

  const handleDateRange = (range: [Date, Date]) => {
    const start = range[0];
    const end = range[1] || start;
    const diffDays = differenceInDays(end, startOn);

    if (diffDays < 0) {
      enqueueSnackbar('終了日は開始日よりも過去に設定できません');
      return;
    }

    if (diffDays > 30) {
      enqueueSnackbar('最大取得可能日数の31日を超過しています');
      return;
    }

    setStartOn(start);
    setEndOn(end);
  };

  useEffect(() => {
    if (startOn) {
      const d = datetimeDecorator.toYyyyMmDd(startOn);
      setStartOnStr((prev) => (prev === d ? prev : d));
    }
    if (endOn) {
      const d = datetimeDecorator.toYyyyMmDd(endOn);
      setEndOnStr((prev) => (prev === d ? prev : d));
    }
    setFetchPage(0);
  }, [startOn, endOn]);

  useEffect(() => {
    if (isPlanningView) return;

    if (startOnStr && endOnStr) {
      void navigate(`/shipment-order-legs/${startOnStr}/${endOnStr}`);
    }
  }, [navigate, startOnStr, endOnStr, isPlanningView]);

  const toggleDialogIsOpen = () => {
    const maybeEditEntity: OrderResponseEntity | undefined = orderEntities.find((order) => order.id === selectedOrderIds.slice(-1)[0]);

    if (maybeEditEntity) {
      setEditTargetOrder(orderRequestUtil.convertToOrderEntity(maybeEditEntity));
    } else {
      setEditTargetOrder(null);
    }

    setDialogIsOpen(!dialogIsOpen);
  };
  const dialogOnClose = () => {
    setDialogIsOpen(false);
  };

  const dataGridOnColumnVisibilityModelChange = (newModel: GridColumnVisibilityModel) => {
    const requestPath = '/api/v2/company';
    if (Object.keys(newModel).length === 0) {
      // 「すべて表示」を選択した場合は空になる。緯度経度カラムを表示にしておかないと、デフォルトで非表示になってしまう。
      Object.keys(DEFAULT_HIDDEN_COLUMNS).forEach((it) => {
        newModel[it] = true;
      });
    }
    const requestBody = {
      order_column_visibilities: newModel
    };
    axios.post(requestPath, requestBody).then(() => {
      setColumnVisibilityModel(newModel);
      dataGridUtil.setSavedColumnVisibilities(localStorageItemPrefix, newModel);
    }).catch((err) => {
      enqueueSnackbar('エラーが発生しました');
      throw err;
    });
  };

  const [orderEditEntity, setOrderEditEntity] = useState<OrderResponseEntity>(null);
  const [loadingCoordinationEditDialogIsOpen, setLoadingCoordinationEditDialogIsOpen] = useState(false);
  const [unloadingCoordinationEditDialogIsOpen, setUnloadingCoordinationEditDialogIsOpen] = useState(false);

  const closeLoadingCoordinationEditDialog = () => {
    setOrderEditEntity(null);
    setLoadingCoordinationEditDialogIsOpen(!loadingCoordinationEditDialogIsOpen);
  };

  const closeUnloadingCoordinationEditDialog = () => {
    setOrderEditEntity(null);
    setUnloadingCoordinationEditDialogIsOpen(!unloadingCoordinationEditDialogIsOpen);
  };

  const openLoadingMapDialog = (entity: OrderResponseEntity) => {
    setOrderEditEntity(entity);
    setLoadingCoordinationEditDialogIsOpen(true);
  };

  const openUnloadingMapDialog = (entity: OrderResponseEntity) => {
    setOrderEditEntity(entity);
    setUnloadingCoordinationEditDialogIsOpen(true);
  };

  const updateLoadingLatLng = (lat: string, lng: string) => {
    const orderEntity = orderRequestUtil.convertToOrderEntity(orderEditEntity);
    orderEntity.loading_latitude = lat;
    orderEntity.loading_longitude = lng;
    orderEntity.update_position = true;

    setIsLoading(true);
    updateOrder.mutate(orderEntity, {
      onSuccess: () => { closeLoadingCoordinationEditDialog(); },
      onError: (e: AxiosError<{ data: string[]; }>) => { enqueueSnackbar(e.response.data.data.join(', ')); },
      onSettled: () => {
        setFetchPage(0);
        void queryClient.resetQueries({
          queryKey: [queryKey]
        }).finally(() => {
          setIsLoading(false);
        });
      }
    });
  };

  const loadingCoordinationEditDialog = (
    orderEditEntity && loadingCoordinationEditDialogIsOpen && (
      <CoordinationEditDialog
        key="loadingCoordinationEditDialog"
        dialogIsOpen={loadingCoordinationEditDialogIsOpen}
        closeDialog={closeLoadingCoordinationEditDialog}
        loading={isLoading}
        lat={orderEditEntity.loading_latitude}
        lng={orderEditEntity.loading_longitude}
        updateLatLng={updateLoadingLatLng}
        isOrder
      />
    )
  );

  const updateUnloadingLatLng = (lat: string, lng: string) => {
    const orderEntity = orderRequestUtil.convertToOrderEntity(orderEditEntity);
    orderEntity.unloading_latitude = lat;
    orderEntity.unloading_longitude = lng;
    orderEntity.update_position = true;

    setIsLoading(true);
    updateOrder.mutate(orderEntity, {
      onSuccess: () => { closeLoadingCoordinationEditDialog(); },
      onError: (e: AxiosError<{ data: string[]; }>) => { enqueueSnackbar(e.response.data.data.join(', ')); },
      onSettled: () => {
        setFetchPage(0);
        void queryClient.resetQueries({
          queryKey: [queryKey]
        }).finally(() => {
          setIsLoading(false);
        });
      }
    });
  };

  const unloadingCoordinationEditDialog = (
    orderEditEntity && unloadingCoordinationEditDialogIsOpen && (
      <CoordinationEditDialog
        key="unloadingCoordinationEditDialog"
        dialogIsOpen={unloadingCoordinationEditDialogIsOpen}
        closeDialog={closeUnloadingCoordinationEditDialog}
        loading={isLoading}
        lat={orderEditEntity.unloading_latitude}
        lng={orderEditEntity.unloading_longitude}
        updateLatLng={updateUnloadingLatLng}
        isOrder
      />
    )
  );

  const updateOrderEntitiesOrderAssignment = (orderId: number, key: keyof OrderAssignment, value: string) => {
    setOrderEntities((prev) => (
      prev.map((it) => {
        if (it.id === orderId) {
          const assignment = JSON.parse(it.order_assignments_json) as OrderAssignment;
          if (assignment) {
            assignment[key as string] = value;
            return {
              ...it,
              order_assignments_json: JSON.stringify(assignment),
            };
          }
        }
        return it;
      })
    ));
  };

  const updateDriverName = useCallback((orderId: number, newName: string) => {
    const driverEntity = driverEntities.find((it) => it.name === newName);
    const order = orderEntities.find((it) => it.id === orderId);
    if (order.order_assignments_json) {
      const orderAssignment = JSON.parse(order.order_assignments_json) as OrderAssignment;
      if ((orderAssignment.driver_name === newName) || (!orderAssignment.driver_name && newName === '')) {
        return;
      }
    } else if (newName === '') {
      return;
    }
    const data = {
      id: null,
      order_id: orderId,
      driver_name: newName,
      driver_id: driverEntity?.id,
    } as OrderAssignment;

    updateOrderAssignment.mutate(data, {
      onSuccess: () => {
        if (driverEntities) {
          setFetchPage(0);
          updateOrderEntitiesOrderAssignment(orderId, 'driver_name', newName);
          void queryClient.invalidateQueries({
            queryKey: ['useQueryPlanningsDeliveries']
          });
        }
      },
      onError: (error: AxiosError<{ message: string; }>) => {
        enqueueSnackbar(error.response.data.message);
      },
    });
  }, [driverEntities, enqueueSnackbar, orderEntities, queryClient, updateOrderAssignment]);

  const updateOrderAssignmentColumns = useCallback((orderId: number, key: string, value: string) => {
    const editableFields = ['carrier_company', 'operationg_carrier', 'hierarchey_level'];
    if (!editableFields.includes(key)) return;

    const order = orderEntities.find((it) => it.id === orderId);
    if (order.order_assignments_json) {
      const orderAssignment = JSON.parse(order.order_assignments_json) as OrderAssignment;
      if ((orderAssignment[key] === value) || (!orderAssignment.driver_name && value === '')) {
        return;
      }
    } else if (value === '') {
      return;
    }
    const data = {
      id: null,
      order_id: orderId,
      [key]: value,
    } as OrderAssignment;

    updateOrderAssignment.mutate(data, {
      onSuccess: () => {
        setFetchPage(0);
        updateOrderEntitiesOrderAssignment(orderId, key as keyof OrderAssignment, value);
      },
      onError: (error: AxiosError<{ message: string; }>) => {
        enqueueSnackbar(error.response.data.message);
      }
    });
  }, [enqueueSnackbar, orderEntities, updateOrderAssignment]);

  useEffect(() => {
    const driverIds = deliveryWithTruckAndDriverEntities?.map((it) => it.driverId) || [];
    setHasNoDeliveryDrivers(driverEntities?.filter((it) => !driverIds.includes(it.id)) || []);
  }, [deliveryWithTruckAndDriverEntities, driverEntities]);

  const dataGridColumns: GridColDef[] = useMemo(() => Columns(
    openLoadingMapDialog,
    openUnloadingMapDialog,
    hasNoDeliveryDrivers,
    updateDriverName,
    partnerCompanyEntities,
    updateOrderAssignmentColumns,
    localStorageItemPrefix,
    rowData,
    apiRef,
  ).filter((col) => {
    if (!licenseContext.config) return true;
    if (licenseContext.config.selectable_companies?.length > 1) return true;

    return col.field !== 'company_name';
  }), [licenseContext.config, hasNoDeliveryDrivers, updateDriverName, partnerCompanyEntities, updateOrderAssignmentColumns, localStorageItemPrefix, rowData, apiRef]);

  const columnsWithCustomFields = useMemo(() => {
    const customInputFields = licenseContext?.config?.custom_input_fields || [];

    const customFields = customInputFields.map((field, index) => (
      {
        field: `custom_input_fields_${index}`,
        headerName: field,
        width: 200,
        valueGetter: (_, row: OrderEntity) => row.custom_input_fields
          ?.find((it) => it.key === field)?.value
      }
    ));
    const orderedFields = dataGridUtil.getColumnOrders(localStorageItemPrefix);

    return [
      ...dataGridColumns,
      ...customFields
    ].sort((a, b) => orderedFields.indexOf(a.field) - orderedFields.indexOf(b.field));
  }, [dataGridColumns, licenseContext?.config?.custom_input_fields, localStorageItemPrefix]);

  // const getCurrentPageRows = () => rowData.slice(page * pageSize, (page + 1) * pageSize);

  const dataGridOnSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    const ids = Array.from(rowSelectionModel.ids).map((id) => Number(id));
    if (rowSelectionModel.type === 'include') {
      setSelectedOrderIds(ids);
    } else if (rowSelectionModel.type === 'exclude') {
      setSelectedOrderIds(requestData.allIds.filter((it) => !ids.includes(it)));
    }
  };

  const removeSelectedOrders = async () => {
    if (!selectedOrderIds.length) return;
    if (!window.confirm(`${selectedOrderIds.length}件の案件を削除します。よろしいですか？`)) return;

    setSelectedOrderIds([]);
    setIsLoading(true);

    try {
      await deleteOrders.mutateAsync(selectedOrderIds);
      enqueueSnackbar(`${selectedOrderIds.length}件の注文を削除しました`);
      setSelectedOrderIds([]);
    } catch (e) {
      enqueueSnackbar('エラーが発生しました');
      throw e;
    } finally {
      setFetchPage(0);
      void queryClient.resetQueries({
        queryKey: [queryKey]
      })
        .finally(() => {
          setIsLoading(false);
          setSelectedOrderIds([]);
        });
      if (resetQuery) {
        resetQuery();
      }
    }
  };

  const resetSelectedOrders = async () => {
    if (!selectedOrderIds.length) return;

    setIsLoading(true);

    const oldOrderEntities = orderEntities;
    try {
      setOrderEntities((prev) => prev.map((it) => ({
        ...it,
        order_assignments_json: selectedOrderIds.includes(it.id) ? null : it.order_assignments_json,
        drivers_json: selectedOrderIds.includes(it.id) ? null : it.drivers_json,
      })));
      await operationBulkResetting.mutateAsync(selectedOrderIds);
      enqueueSnackbar(`${selectedOrderIds.length}件の注文を割当解除しました`);
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as unknown as AxiosError<{ message: string }>;
        enqueueSnackbar(axiosError.response?.data?.message || fiveZeroZeroErrorMessage);
      } else {
        enqueueSnackbar('リセット処理でエラーが発生しました。');
      }
      setOrderEntities(oldOrderEntities);
    } finally {
      setFetchPage(0);
      void queryClient.resetQueries({
        queryKey: [queryKey]
      }).finally(() => {
          setIsLoading(false);
          setSelectedOrderIds([]);
        });
      if (afterResetOrders) {
        afterResetOrders();
      }
    }
  };

  const toggleSplitDialogIsOpen = () => {
    setSplitDialogIsOpen(!splitDialogIsOpen);
  };

  const splitDialogOnClose = () => {
    setSplitDialogIsOpen(false);
  };

  const onClickSplit = () => {
    toggleSplitDialogIsOpen();
  };

  const onSplitDialogSubmit = () => {
    dialogOnClose();
  };

  const onRowDoubleClick = (order: OrderResponseEntity) => {
    setEditTargetOrder(
      orderRequestUtil.convertToOrderEntity(order)
    );
    setDialogIsOpen(true);
  };

  const fieldNameThatNeedsToBeRenamed = [
    {
      current: 'item_total_weight_kg',
      server: 'item_total_weight_for_calculation',
      convert: numberUtil.convertFromKgToGram,
    },
    {
      current: 'item_total_volume_m3',
      server: 'item_total_volume_mm3',
      convert: numberUtil.convertFromM3ToMm3,
    },
    {
      current: 'loading_staying_minutes',
      server: 'loading_staying_seconds',
      convert: numberUtil.convertFromMinutesToSeconds,
    },
    {
      current: 'unloading_staying_minutes',
      server: 'unloading_staying_seconds',
      convert: numberUtil.convertFromMinutesToSeconds,
    },
  ];

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }
    const maybeServerFieldName = fieldNameThatNeedsToBeRenamed.find((it) => it.current === stringUtil.toSnakeCase(sortModel[0].field))?.server;
    const field = maybeServerFieldName || sortModel[0].field;

    setSortCondition(
      [
        stringUtil.toSnakeCase(field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const convertedItems = items.map((it) => {
      if (['charge_basic_fee_yen', 'charge_highway_fee_yen', 'charge_loading_fee_yen',
           'charge_ancillary_fee_yen', 'charge_waiting_time_fee_yen', 'charge_unloading_fee_yen',
           'charge_expenses_fee_yen',
           'item_total_volume_m3', 'loading_staying_minutes', 'unloading_staying_minutes'].includes(it.field || '')) {
        if (it.operator === 'isNotEmpty') {
          return { ...it, operator: 'isNotNull' };
        }
        if (it.operator === 'isEmpty') {
          return { ...it, operator: 'isNull' };
        }
        return it;
      }
      return it;
    });

    const ransackPredicates: RansackPredicateEntity[] = convertedItems.flatMap((item: GridFilterItem) => {
      const maybeServerField = fieldNameThatNeedsToBeRenamed.find((it) => it.current === stringUtil.toSnakeCase(item.field || ''));

      if (!maybeServerField) {
        return operatorValueUtil.convertToRansackPredicate(
          stringUtil.toSnakeCase(item.field || ''),
          item.operator || '',
          item.value as string | string[] | undefined,
          filterModel.logicOperator || 'and'
        );
      }

      const maybeValue = parseFloat(item.value as string);

      return operatorValueUtil.convertToRansackPredicate(
        maybeServerField.server,
        item.operator || '',
      maybeValue ? maybeServerField.convert(maybeValue).toString() : '0',
        filterModel.logicOperator || 'and'
      );
    }).filter((maybe) => !!maybe);

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  useEffect(() => {
    setStartOn(new Date(startDate));
    setEndOn(new Date(endDate));
  }, [startDate, endDate]);

  useEffect(() => {
    const lastSelectedEntity = orderEntities.find((order) => order.id === selectedOrderIds.slice(-1)[0]);
    if (!lastSelectedEntity) return;

    setOrderSplitEntity({
      order_id: lastSelectedEntity.id,
      original_item_count: lastSelectedEntity.item_count,
      original_item_total_weight_kg: numberUtil.convertFromGramToKg(lastSelectedEntity.item_total_weight_for_calculation),
      original_item_total_volume_m3: numberUtil.convertFromMm3ToM3(lastSelectedEntity.item_total_volume_mm3),
      is_item_total_volume_m3_required: !!lastSelectedEntity.item_total_volume_mm3
    });
  }, [orderEntities, selectedOrderIds]);

  useEffect(() => {
    if (!licenseContext.config) return;

    let visibilityModel = dataGridUtil.getSavedColumnVisibilities(localStorageItemPrefix);
    if (Object.keys(visibilityModel).length === 0) {
      visibilityModel = licenseContext.config?.order_column_visibilities || DEFAULT_HIDDEN_COLUMNS;
    }
    Object.keys(DEFAULT_HIDDEN_COLUMNS).forEach((it) => {
      if (!(it in visibilityModel)) {
        visibilityModel[it] = false;
      }
    });
    if (visibilityModel) {
      setColumnVisibilityModel(visibilityModel);
    }
  }, [DEFAULT_HIDDEN_COLUMNS, licenseContext.config, localStorageItemPrefix]);

  useEffect(() => {
    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', andConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions]);

  useEffect(() => {
    if (!requestData) return;
    setRowCount(requestData.totalCount);
  }, [requestData]);

  useEffect(() => {
    const canPaginate = rowCount > pageSize;

    if (!canPaginate) {
      setCanDeleteAll(false);
    }

    const selectedAll = selectedOrderIds?.length === pageSize;

    setCanDeleteAll(selectedAll);
  }, [selectedOrderIds, pageSize, rowCount]);

  const handleDownloadExcel = async () => {
    // if (!allRequestData?.data) return;

    const options = {
      fileName: `LOG-案件一覧-${format(new Date(), 'yyyyMMddHHmmss', { timeZone: 'Asia/Tokyo' })}`,
    };
    apiRef.current.getAllColumns().forEach((column) => {
      if (column.type === 'date') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (column as any).type = 'string';
      }
    });

    apiRef.current.setRows(orderEntities);
    await apiRef.current.exportDataAsExcel(options);
    // apiRef.current.setRows(requestData.data);
  };

  // 使用していない関数を削除

  useEffect(() => {
    localStorage.setItem('planningOn', startOnStr);
  }, [startOnStr]);

  const dataGridHgieht = useMemo(() => {
    let offset = appBarHeight;
    if (isPlanningView) {
      offset += actionBarHeight;
    }
    const spacing = isPlanningView ? 2 : 4;

    return `calc(100vh - ${theme.spacing(spacing)} - ${offset}px)`;
  }, [isPlanningView, theme]);

  const onColumnOrderChange = useCallback(() => {
    const columnOrders = apiRef.current.getAllColumns().map((col) => col.field);
    dataGridUtil.setColumnOrders(localStorageItemPrefix, columnOrders);
  }, [apiRef, localStorageItemPrefix]);

  useEffect(() => {
    if (!setOrderGridApiRef) return;
    setOrderGridApiRef(apiRef);
  }, [apiRef, setOrderGridApiRef]);

  useEffect(() => {
    if (!orderEntities) return;

    setRowData(
      orderEntities.map((it) => ({
        ...it,
        notAllocReasons: plansContext.notAllocReasons?.filter((reason) => reason.order_id === it.id) || [],
        notAllocDeliveries,
        notAllocDrivers,
        notAllocTrucks,
      }))
    );
  }, [notAllocDeliveries, notAllocDrivers, plansContext.notAllocReasons, notAllocTrucks, orderEntities]);

  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; rowData: OrderResponseEntity; anchorEl: HTMLElement } | null>(null);

  const onCellClick = useCallback((params: { field: string; row: OrderResponseEntityWithNotAllocReason; rowNode: { type?: string } }, event: React.MouseEvent) => {
    if (!isPlanningView) return;
    if (params.rowNode?.type === 'group') return;

    if (params.field === 'drivers_name') {
      event.preventDefault();
      event.stopPropagation();

      if (params.row.order_operations_fixed) return;

      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        rowData: params.row,
        anchorEl: event.currentTarget.firstChild as HTMLElement,
      });
    }
  }, [isPlanningView]);

  const handleClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handlePlanning = useCallback((delivery: PlanningsDeliveryWithTruckAndDriverEntity) => {
    const orderId = contextMenu.rowData.id;
    const orderIds = selectedOrderIds.includes(orderId) ? selectedOrderIds : [orderId];
    const orderAssignmentIds = orderEntities.filter((it) => orderIds.includes(it.id))
      .map((it) => JSON.parse(it.order_assignments_json) as OrderAssignment).map((it) => it.id).filter((it) => it);

    let confirmMessage = `${orderIds.length}件の案件を${orderIds.length > 1 ? '一括' : ''}配車します。\n`;
    if (orderAssignmentIds.length > 0) {
      confirmMessage = `${confirmMessage}運送会社、車番、ドライバーの情報は上書きされます。\n`;
    }

    if (!window.confirm(`${confirmMessage}よろしいですか？`)) {
      return;
    }

    const { truck, driver } = delivery;
    setOrderEntities((prev) => prev.map((it) => {
      if (orderIds.includes(it.id)) {
        return {
          ...it,
          order_operations_count: 1,
          truck_json: JSON.stringify([{
            id: truck?.id,
            license_plate_value: truck?.licensePlateValue,
            temporary: truck?.temporary,
          }]),
          drivers_json: JSON.stringify([{
            id: driver?.id,
            name: driver?.name,
            temporary: driver?.temporary,
          }]),
          order_assignments_json: JSON.stringify({
            id: 1,
            assignment: truck?.temporary ? '車両未設定' : truck?.licensePlateValue,
            truck_number: truck?.truckNumber,
            driver_name: driver?.name,
          }),
        };
      }
      return it;
    }));
    ReactGA.event('executeForcePlanning', { screen_name: SCREEN_NAMES.ORDER_LIST });
    requestForcePlanning(delivery, orderIds);
    setContextMenu(null);
  }, [contextMenu?.rowData, requestForcePlanning, selectedOrderIds, orderEntities]);

  const handlePartnerCompany = useCallback((partnerCompany: PartnerCompanyEntity) => {
    const orderId = contextMenu.rowData.id;
    const orderIds = selectedOrderIds.includes(orderId) ? selectedOrderIds : [orderId];
    const orderAssignmentIds = orderEntities.filter((it) => orderIds.includes(it.id))
      .map((it) => JSON.parse(it.order_assignments_json) as OrderAssignment).map((it) => it.id).filter((it) => it);

    let confirmMessage = `${orderIds.length}件の案件を${orderIds.length > 1 ? '一括' : ''}配車します。\n`;
    if (orderAssignmentIds.length > 0) {
      confirmMessage = `${confirmMessage}運送会社、車番、ドライバーの情報は上書きされます。\n`;
    }

    if (!window.confirm(`${confirmMessage}よろしいですか？`)) {
      return;
    }

    const data = {
      id: null,
      order_id: orderId,
      assignment: partnerCompany.name,
      truck_number: null,
      use_ltl: true,
      carrier_company: partnerCompany.name,
      operationg_carrier: null,
      hierarchey_level: null,
      selected_order_ids: orderIds,
    } as OrderAssignment;
    setOrderEntities((prev) => prev.map((it) => {
      if (it.id === orderId) {
        const assignment = JSON.parse(it.order_assignments_json) as OrderAssignment;
        const newAssingment: OrderAssignment = {
          ...assignment,
          assignment: partnerCompany.name,
          use_ltl: true,
          carrier_company: partnerCompany.name,
        };

        return {
          ...it,
          order_assignments_json: JSON.stringify(newAssingment),
        };
      }
      return it;
    }));
    updateOrderAssignment.mutate(data, {
      onSettled: () => {
        setFetchPage(0);
      },
      onError: (error: AxiosError<{ message: string; }>) => {
        enqueueSnackbar(error.response.data.message);
      }
    });
    setContextMenu(null);
  }, [contextMenu?.rowData?.id, enqueueSnackbar, orderEntities, selectedOrderIds, updateOrderAssignment]);

  useEffect(() => {
    if (!partnerCompanyEntities) return;

    setLtlCarrier(partnerCompanyEntities.filter((it) => it.category === '路線会社'));
  }, [partnerCompanyEntities]);

  const contextMenuMemo = useMemo(() => {
    if (!isPlanningView) return null;

    const truckName = (truck: PlanningsTruckEntity) => {
      if (!truck) return '';

      const name = truck.temporary ? '車両未設定' : truck.licensePlateValue;
      const weight = numberDecorator.convertGramToKg(truck.maximumLoadingCapacityWeightForCalculation);
      const volume = numberDecorator.convertMm3ToM3(truck.loadingPlatformVolumeMm3);
      const weightAndVolume = [weight, volume].filter((it) => it).join('/');
      return `${name} (${weightAndVolume})`;
    };

    const ratio = (delivery: PlanningsDeliveryWithTruckAndDriverEntity) => {
      if (delivery.operations.length === 0) {
        return <Chip color="error" size="small" label="計画無" />;
      }
      const model = new Operations(licenseContext.config, delivery.operations);
      const entity = model.createPlanningsOperationDeliveryByDeliveryIdEntity()[delivery.id];
      const { truck } = delivery;
      if (!truck) {
        return '';
      }

      const weightRatio = [
        numberDecorator.convertGramToKg(entity.maxWeightG),
      ].join('/');
      const volumeRatio = (entity.maxVolumeMm3 && truck.loadingPlatformVolumeMm3)
      ? [
          numberDecorator.convertMm3ToM3(entity.maxVolumeMm3, 1),
        ].join('/')
      : null;
      return [weightRatio, volumeRatio].filter((r) => r).join(', ');
    };

    const MAX_SORT_ORDER = 999999;
    const sortedEntities = deliveryWithTruckAndDriverEntities.sort((a, b) => {
      if (!a.truck || !b.truck) return 0;

      let ret = 0;
      const aTruckSortOrder = a.truck.sortOrder ?? MAX_SORT_ORDER;
      const bTruckSortOrder = b.truck.sortOrder ?? MAX_SORT_ORDER;
      ret = aTruckSortOrder - bTruckSortOrder;
      if (ret !== 0) {
        return ret;
      }

      const aDriverSortOrder = a.driver?.sortOrder ?? MAX_SORT_ORDER;
      const bDriverSortOrder = b.driver?.sortOrder ?? MAX_SORT_ORDER;
      ret = aDriverSortOrder - bDriverSortOrder;
      if (ret !== 0) {
        return ret;
      }
      return a.truck.id - b.truck.id;
    });

    const menuItems = sortedEntities.map((delivery) => (
      <TableBody
        key={['TableBody', delivery.id, delivery.truck?.id, delivery.driver?.id].join('-')}
      >
        <TableRow
          sx={{ p: 1, m: 0, }}
          key={[delivery.id, delivery.truck?.id, delivery.driver?.id].join('-')}
        >
          <TableCell
            align="left"
            sx={{ p: 1, m: 0, }}
          >
            {delivery.driver?.temporary ? 'ドライバー未設定' : delivery.driver?.name}
          </TableCell>
          <TableCell
            align="left"
            sx={{ p: 1, m: 0, }}
          >
            {truckName(delivery.truck)}
          </TableCell>
          <TableCell
            align="left"
            sx={{ p: 1, m: 0, }}
          >
            {ratio(delivery)}
          </TableCell>
          <TableCell
            sx={{
              wordBreak: 'keep-all',
              p: 0,
            }}
          >
            <span>
              <Button
                onClick={() => handlePlanning(delivery)}
              >
                配車する
              </Button>
            </span>
          </TableCell>
        </TableRow>
      </TableBody>
    ));

    const deliveryDriverIds = deliveryWithTruckAndDriverEntities.map((it) => it.driverId);
    // 仮トラック・仮ドライバーはデリバリーに含まれるので除外する
    const driverTrucks = driverEntities.filter((it) => !it.temporary).filter((it) => !deliveryDriverIds.includes(it.id)).map((it) => {
      const truck = truckEntities.find((t) => t.id === it.defaultTruckId);
      if (truck) {
        return { driver: it, truck };
      }
      return null;
    }).filter((it) => it);

    const driverTruckMenuItems = driverTrucks.map(({ driver, truck }) => {
      const delivery = {
        id: -1,
        shiftId: -1,
        truckId: truck.id,
        startAt: datetimeDecorator.toYyyyMmDdHhMm(new Date(`${startDate} 00:00`)),
        endAt: datetimeDecorator.toYyyyMmDdHhMm(new Date(`${startDate} 23:59`)),
        driverId: driver.id,
        operations: [],
        arrivalDistanceMm: 0,
        garageLatitude: null,
        garageLongitude: null,
        driverColor: null,
        distanceMmBurdenPerShippers: [],
        driver,
        truck,
      } as PlanningsDeliveryWithTruckAndDriverEntity;

      return (
        <TableBody
          key={['TableBody', driver.id, truck.id].join('-')}
        >
          <TableRow
            sx={{ p: 1, m: 0, }}
            key={[truck.id, driver.id].join('-')}
          >
            <TableCell
              align="left"
              sx={{ p: 1, m: 0, }}
            >
              {driver.temporary ? 'ドライバー未設定' : driver.name}
            </TableCell>
            <TableCell
              align="left"
              sx={{ p: 1, m: 0, }}
            >
              {truckName(truck)}
            </TableCell>
            <TableCell
              align="left"
              sx={{ p: 1, m: 0, }}
            >
              <Chip color="error" size="small" label="計画無" />
            </TableCell>
            <TableCell
              sx={{
                wordBreak: 'keep-all',
                p: 0,
              }}
            >
              <span>
                <Button
                  onClick={() => handlePlanning(delivery)}
                >
                  配車する
                </Button>
              </span>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    });

    const partnerCompanyItems = ltlCarriers.map((company) => (
      <TableBody
        key={['TableBody', 'partnerCompanyItems', company.id].join('-')}
      >
        <TableRow
          sx={{ p: 1, m: 0, }}
          key={['partnerCompanyItems', company.id].join('-')}
        >
          <TableCell
            align="left"
            sx={{ p: 1, m: 0, }}
          >
            {company.name}
          </TableCell>
          <TableCell
            sx={{
              wordBreak: 'keep-all',
              p: 0,
            }}
          >
            <span>
              <Button
                onClick={() => handlePartnerCompany(company)}
              >
                設定する
              </Button>
            </span>
          </TableCell>
        </TableRow>
      </TableBody>
    ));

    return (
      <Popover
        open={contextMenu !== null}
        onClose={handleClose}
        anchorEl={contextMenu !== null ? contextMenu.anchorEl : undefined}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          p: 1,
          m: 0,
          maxWidth: '90%',
          maxHeight: '90%',
        }}
      >
        {ltlCarriers.length > 0 && (
          <Table
            sx={{
              p: 1,
              m: 0,
            }}
          >
            <TableHead>
              <TableRow
                sx={{ p: 1, m: 0, }}
              >
                <TableCell>
                  路線会社
                </TableCell>
                <TableCell>
                  配車
                </TableCell>
              </TableRow>
            </TableHead>
            {partnerCompanyItems}
          </Table>
        )}
        {ltlCarriers.length > 0 && sortedEntities.length > 0 && (
          <Divider sx={{ m: 2 }} />
        )}
        {(sortedEntities.length > 0 || driverTrucks.length > 0) && (
          <Table
            sx={{
              p: 1,
              m: 0,
            }}
          >
            <TableHead>
              <TableRow
                sx={{ p: 1, m: 0, }}
              >
                <TableCell>ドライバー</TableCell>
                <TableCell>トラック</TableCell>
                <TableCell>割当済</TableCell>
                <TableCell>配車</TableCell>
              </TableRow>
            </TableHead>
            {menuItems}
            {driverTruckMenuItems}
          </Table>
        )}
      </Popover>
    );
  }, [isPlanningView, deliveryWithTruckAndDriverEntities, driverEntities, ltlCarriers, contextMenu, handleClose, licenseContext.config, handlePlanning, truckEntities, startDate, handlePartnerCompany]);

  const onCellEditStop = (params: GridCellEditStopParams) => {
    const editableFields = ['truck_number'];
    if (!editableFields.includes(params.field)) return;

    const order = orderEntities.find((it) => it.id === params.id);
    if (!order) return;

    if (order.order_assignments_json) {
      const orderAssingment = JSON.parse(order.order_assignments_json) as OrderAssignment;
      if ((orderAssingment.truck_number === params.value) || (!orderAssingment.truck_number && params.value === '')) {
        return;
      }
    } else if (params.value === '') {
      return;
    }

    const newValue = String(params.value);
    const data = {
      id: null,
      order_id: params.id,
      [params.field]: !newValue ? null : newValue,
    } as OrderAssignment;

    updateOrderAssignment.mutate(data, {
      onSuccess: () => {
        setFetchPage(0);
        updateOrderEntitiesOrderAssignment(Number(params.id), params.field as keyof OrderAssignment, String(params.value));
      },
      onError: (error: AxiosError<{ message: string; }>) => {
        enqueueSnackbar(error.response?.data?.message || 'エラーが発生しました');
      }
    });
  };

  const onCellDoubleClick = (params: GridCellParams, event: React.MouseEvent) => {
    if (params.rowNode?.type && params.rowNode.type === 'group') return;

    const order = params.row as OrderResponseEntity;
    if (!isPlanningView || !params.isEditable) {
      event.stopPropagation();
      if (!params.isEditable) {
        onRowDoubleClick(params.row as OrderResponseEntity);
      }
    }
  };

  useEffect(() => {
    const localStorageDensityKey = `${localStorageItemPrefix}-density`;
    const savedDensity = localStorage.getItem(localStorageDensityKey) as GridDensity;
    if (savedDensity) {
      apiRef.current.setDensity(savedDensity);
    }

    // 密度の変更を監視
    const intervalId = setInterval(() => {
      const { density } = apiRef.current.state;
      const storedDensity = localStorage.getItem(localStorageDensityKey);

      if (density !== storedDensity) {
        localStorage.setItem(localStorageDensityKey, density);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [apiRef, localStorageItemPrefix]);

  const [downloadOutsourcingPdfSettingsIsOpen, setDownloadOutsourcingPdfSettingsIsOpen] = useState(false);

  const handlePdfSettingsClose = () => {
    setDownloadOutsourcingPdfSettingsIsOpen(false);
  };

  const onClickDownloadOutsourcingPdf = () => {
    if (selectedOrderIds.length === 0) {
      enqueueSnackbar('出力する案件を選択してください。');
      return;
    }
    setDownloadOutsourcingPdfSettingsIsOpen(true);
  };

  const pdfDialog = useMemo(() => (
    <PdfSettingDialog
      orderIds={selectedOrderIds}
      downloadOutsourcingPdfSettingsIsOpen={downloadOutsourcingPdfSettingsIsOpen}
      handlePdfSettingsClose={handlePdfSettingsClose}
    />
  ), [downloadOutsourcingPdfSettingsIsOpen, selectedOrderIds]);

  return (
    <>
      {!isPlanningView && (
        <title>配車一覧</title>
      )}
      <Box p={isPlanningView ? 1 : 2}>
        {loadingCoordinationEditDialog}
        {unloadingCoordinationEditDialog}
        {
          dialogIsOpen && (
            <ShipmentOrderLegForm
              dialogIsOpen={dialogIsOpen}
              dialogOnClose={dialogOnClose}
              onClose={dialogOnClose}
              onClickSplit={onClickSplit}
              startOn={format(startOn, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' })}
              entity={
                null // editTargetOrder
              }
              createAfterCallbackFnc={() => setFetchPage(0)}
            />
          )
        }
        {
          splitDialogIsOpen && (
            <Dialog
              open={splitDialogIsOpen}
              maxWidth="md"
              fullWidth
              onClose={splitDialogOnClose}
            >
              <DialogContent>
                <OrderSplitForm
                  onClose={splitDialogOnClose}
                  createAfterCallbackFnc={onSplitDialogSubmit}
                  entity={orderSplitEntity}
                />
              </DialogContent>
            </Dialog>
          )
        }
        {selectedOrderIds.length > 0 && pdfDialog}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <SelectAllSnackbars
            totalPageCount={Math.ceil(orderEntities.length / pageSize) || 1}
            totalCount={rowCount || 0}
            perPage={pageSize}
            allIds={orderEntities.map((it) => it.id)}
            selectedIds={selectedOrderIds}
            apiRef={apiRef}
            setSelectedIds={setSelectedOrderIds}
          />
          <Box flexGrow={1} sx={{ width: '100%', }}>
            <Paper
              style={{
                width: '100%',
                height: dataGridHgieht,
              }}
            >
              <DataGridPremium
                apiRef={apiRef}
                rows={rowData}
                columns={columnsWithCustomFields}
                pagination
                checkboxSelection
                showToolbar
                slots={{
                  loadingOverlay: CustomLoadingOverlay,
                  toolbar: CustomToolbar,
                }}
                slotProps={{
                  toolbar: {
                    startOn,
                    endOn,
                    removeSelectedOrders,
                    resetSelectedOrders,
                    toggleDialogIsOpen,
                    setIsLoading,
                    isLoading,
                    handleDateRange,
                    canDeleteAll,
                    deleteAll,
                    handleDownloadExcel,
                    isPlanningView,
                    setFetchPage,
                    onClickDownloadOutsourcingPdf,
                  } as Partial<CustomToolbarProps>,
                }}
                rowHeight={defaultRowHeight}
                localeText={jaJP.components.MuiDataGrid.defaultProps?.localeText}
                loading={requestIsLoading || isLoading}
                onRowSelectionModelChange={dataGridOnSelectionModelChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={dataGridOnColumnVisibilityModelChange}
                // rowSelectionModel={selectedOrderIds}
                sortingMode="client"
                paginationMode="client"
                filterMode="client"
                paginationModel={{
                  page,
                  pageSize,
                }}
                // rowCount={rowCount}
                onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
                  setPage(newPage);
                  setPageSize(newPageSize);
                  setSelectedOrderIds([]);
                }}
                onSortModelChange={handleSortModelChange}
                onFilterModelChange={onFilterChange}
                onColumnWidthChange={(params: GridColumnResizeParams) => {
                  dataGridUtil.setSavedColumnWidth(localStorageItemPrefix, params);
                }}
                keepNonExistentRowsSelected
                onColumnOrderChange={onColumnOrderChange}
                onCellClick={onCellClick}
                editMode="cell"
                onCellDoubleClick={onCellDoubleClick}
                onCellEditStop={onCellEditStop}
                // onRowGroupingModelChange={(model) => setGroupBy(model)}
                // treeData
                // getTreeDataPath={(row: OrderResponseEntity) => (row.treePath ? row.treePath : [])}
                // groupingColDef={{
                //   renderCell: (params: GridRenderCellParams) => {
                //     console.log(params.rowNode);
                //     // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                //     return params.value;
                //   }
                // }}
                disableAggregation
                disablePivoting
              />
            </Paper>
            {contextMenuMemo}
          </Box>
        </Box>
      </Box>
    </>
  );
})
);

export default V2ShipmentOrderLegsPresenter;
